import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Users, ChevronRight, Star } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveOrg } from '@/hooks/useActiveOrg';

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
}

export default function OrgsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { memberships, activeOrgId, setActiveOrgId, loading, refetch } = useActiveOrg();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setSlug(slugify(name));
  }, [name]);

  const onCreate = async () => {
    if (!user || !name.trim()) return;
    setCreating(true);
    const { data, error } = await supabase
      .from('organizations')
      .insert({ name: name.trim(), slug: slug || slugify(name), created_by: user.id })
      .select('id')
      .single();
    setCreating(false);
    if (error || !data) {
      toast.error(error?.message ?? 'Could not create organization');
      return;
    }
    setActiveOrgId(data.id);
    await refetch();
    toast.success('Organization created');
    setCreateOpen(false);
    setName('');
    navigate(`/orgs/${data.id}`);
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-10">
      <Helmet>
        <title>Organizations</title>
        <meta name="description" content="Manage your organizations and team members." />
      </Helmet>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Organizations</h1>
          <p className="text-sm text-muted-foreground">
            Workspaces for teaching teams. Tests, lists and schedules can be scoped to an org so colleagues can collaborate.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="mr-2 h-4 w-4" /> New organization</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create an organization</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="org-name">Name</Label>
                <Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Tutoring" />
              </div>
              <div>
                <Label htmlFor="org-slug">Slug</Label>
                <Input id="org-slug" value={slug} onChange={(e) => setSlug(slugify(e.target.value))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={onCreate} disabled={creating || !name.trim()}>
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {memberships.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-4">You don't belong to any organization yet.</p>
            <Button onClick={() => setCreateOpen(true)}><Plus className="mr-2 h-4 w-4" />Create your first org</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {memberships.map((m) => {
            const isActive = m.org_id === activeOrgId;
            return (
              <Card key={m.org_id} className={isActive ? 'border-primary' : ''}>
                <CardHeader className="flex flex-row items-center justify-between gap-4 py-4">
                  <div className="min-w-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      {m.org_name}
                      {isActive && <Star className="h-4 w-4 text-primary" />}
                    </CardTitle>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      <Badge variant="secondary" className="capitalize">{m.role}</Badge>
                      <span>/{m.org_slug}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isActive && (
                      <Button variant="outline" size="sm" onClick={() => setActiveOrgId(m.org_id)}>
                        Set active
                      </Button>
                    )}
                    <Button asChild size="sm" variant="ghost">
                      <Link to={`/orgs/${m.org_id}`}>
                        Manage <ChevronRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
